/**
 * Invite links and spoken invite codes (docs/design/social-plan.md 4.7) -- pure logic, no I/O. The
 * edge (apps/edge/src/invites.ts) stores and enforces; this is where the rules are written once.
 */
import {
  JOIN_CODE_SEPARATOR,
  WORDS_PER_JOIN_CODE,
  isJoinCodeWord,
  normalizeJoinCode,
  splitJoinCodeWords,
} from "../joinCode.js";
import type { WhoCanView } from "./group.js";
import type { Rsvp } from "./sessionHub.js";
import type { TastingSessionStatus } from "./tastingSession.js";

export type InviteKind = "friend" | "group" | "session";

/** Three words from the join-code list: short enough to say across a table, and a flight code
 * (six words) can never be mistaken for one. */
export const WORDS_PER_INVITE_CODE = 3;

/** A spoken code is guessable in a way a 128-bit token is not, so it lives a day at most. */
export const INVITE_CODE_TTL_MS = 86_400_000;

export const INVITE_TTL_DAYS = [1, 7, 30] as const;
export type InviteTtlDays = (typeof INVITE_TTL_DAYS)[number];
export const DEFAULT_INVITE_TTL_DAYS: InviteTtlDays = 7;

export const DEFAULT_FRIEND_LINK_USES = 1;
/** Above one use every redemption becomes a request the inviter confirms, so ten is plenty. */
export const MAX_FRIEND_LINK_USES = 10;
export const DEFAULT_GROUP_LINK_USES = 25;
export const MAX_GROUP_LINK_USES = 100;
/** A session link is for one table, so fifty seats is the cap and the default alike. */
export const DEFAULT_SESSION_LINK_USES = 50;
export const MAX_SESSION_LINK_USES = 50;
/** A link to a session planned far ahead still has to lapse, whatever the plan says. */
export const MAX_SESSION_LINK_HORIZON_DAYS = 90;

/** Live links one person may hold per kind: enough for a few chats, too few to spray. */
export const MAX_ACTIVE_INVITES_PER_KIND = 5;
/** Links one person may create per day, across kinds. */
export const INVITES_PER_DAY = 50;

/** `null` for anything but exactly three known words, however they were typed. */
export function normalizeInviteCode(input: string): string | null {
  const words = splitJoinCodeWords(input);
  if (words.length !== WORDS_PER_INVITE_CODE || !words.every(isJoinCodeWord)) return null;
  return words.join(JOIN_CODE_SEPARATOR);
}

export type SpokenCode =
  | { readonly kind: "flight"; readonly code: string }
  | { readonly kind: "invite"; readonly code: string };

/** "Have a code?" takes either: the word count alone says which, so one field serves both. */
export function classifySpokenCode(input: string): SpokenCode | null {
  const words = splitJoinCodeWords(input);
  if (words.length === WORDS_PER_JOIN_CODE) {
    const code = normalizeJoinCode(input);
    return code === null ? null : { kind: "flight", code };
  }
  const code = normalizeInviteCode(input);
  return code === null ? null : { kind: "invite", code };
}

export interface InviteLimits {
  readonly expiresAt: string;
  readonly revokedAt: string | null;
  readonly uses: number;
  readonly maxUses: number;
}

export type InviteState = "active" | "revoked" | "expired" | "used_up";

/** Why a link no longer works, or that it does. The order matters only for the inviter's own list;
 * the invitee is told the same thing for every dead link. */
export function inviteState(invite: InviteLimits, now: Date): InviteState {
  if (invite.revokedAt !== null) return "revoked";
  if (Date.parse(invite.expiresAt) <= now.getTime()) return "expired";
  if (invite.uses >= invite.maxUses) return "used_up";
  return "active";
}

/** A code dies after a day, or with its link if that comes first. */
export function inviteCodeExpiry(createdAt: Date, linkExpiresAt: Date): Date {
  return new Date(Math.min(createdAt.getTime() + INVITE_CODE_TTL_MS, linkExpiresAt.getTime()));
}

export type FriendInviteOutcome = "friends" | "requested";

/** A single-use link was meant for one person, so accepting it is the friendship. A multi-use link
 * may travel further than intended, so each redemption waits for the inviter. */
export function friendInviteOutcome(maxUses: number): FriendInviteOutcome {
  return maxUses <= 1 ? "friends" : "requested";
}

/** A members-only group hides even its name from a link preview, so joining through a link waits
 * for an admin unless one turned that off. */
export function defaultRequireApproval(whoCanView: WhoCanView): boolean {
  return whoCanView === "members_only";
}

const DAY_MS = 86_400_000;

/** Sessions carry no planned end, so a link outlives the start by the default week and the host
 * ending the session is what really closes it (`sessionLinkLive`). */
export function sessionInviteExpiry(now: Date, scheduledAt: string | null): Date {
  const start =
    scheduledAt === null ? now.getTime() : Math.max(now.getTime(), Date.parse(scheduledAt));
  return new Date(
    Math.min(
      start + DEFAULT_INVITE_TTL_DAYS * DAY_MS,
      now.getTime() + MAX_SESSION_LINK_HORIZON_DAYS * DAY_MS,
    ),
  );
}

/** A session link dies when the session ends or is called off, which is also an end. */
export function sessionLinkLive(status: TastingSessionStatus): boolean {
  return status !== "ended";
}

/** Whoever is at the table may pass the link on, except someone who said they are not coming. */
export function canMintSessionLink(input: {
  readonly isHost: boolean;
  readonly isParticipant: boolean;
  readonly rsvp: Rsvp | null;
}): boolean {
  return input.isHost || (input.isParticipant && input.rsvp !== "no");
}
